<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useStore } from "vuex";
import {
  fetchDashboardPosts,
  createDashboardPost,
  updateDashboardPost,
  deleteDashboardPost,
  createDashboardPostComment,
  updateDashboardPostComment,
  deleteDashboardPostComment,
  toggleDashboardPostReaction,
} from "@/services/backofficeClient";

const PAGE_SIZE = 30;
const COLLAPSED_COMMENT_COUNT = 3;
const FALLBACK_REACTIONS = ["👍", "❤️", "🎉", "🔥", "👏", "🙌", "🚀"];

const store = useStore();
const myName = computed(
  () => store.getters.currentAdmin?.name || "You"
);

const posts = ref([]);
const availableReactions = ref(FALLBACK_REACTIONS);
const maxTextLength = ref(2000);
const maxCommentTextLength = ref(1000);
const listLoading = ref(true);
const listError = ref("");
const currentPage = ref(1);
const pagination = ref({
  page: 1,
  pageSize: PAGE_SIZE,
  totalItems: 0,
  totalPages: 0,
  hasNext: false,
});

const draft = ref("");
const submitting = ref(false);
const submitError = ref("");

const commentDrafts = reactive({});
const commentSubmitting = reactive({});
const commentErrors = reactive({});
const expandedComments = reactive({});
const openReactionPicker = ref(null);

const editingPostId = ref(null);
const postEditDraft = ref("");
const postEditSaving = ref(false);
const postEditError = ref("");

const editingCommentId = ref(null);
const commentEditDraft = ref("");
const commentEditSaving = ref(false);
const commentEditError = ref("");

const totalPages = computed(() =>
  Math.max(pagination.value.totalPages || 0, 1)
);

const remainingChars = computed(
  () => maxTextLength.value - draft.value.trim().length
);

const formatWhen = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const diffMinutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (diffMinutes < 1) return "just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffMinutes < 24 * 60) return `${Math.round(diffMinutes / 60)}h ago`;
  if (diffMinutes < 7 * 24 * 60)
    return `${Math.round(diffMinutes / 1440)}d ago`;

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const initialsOf = (name) => {
  const parts = String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const visibleComments = (post) => {
  if (expandedComments[post.id]) return post.comments;
  return post.comments.slice(-COLLAPSED_COMMENT_COUNT);
};

const hiddenCommentCount = (post) =>
  expandedComments[post.id]
    ? 0
    : Math.max(post.comments.length - COLLAPSED_COMMENT_COUNT, 0);

const loadPosts = async (page = currentPage.value) => {
  listLoading.value = true;
  listError.value = "";
  try {
    const payload = await fetchDashboardPosts({ page, pageSize: PAGE_SIZE });
    posts.value = (payload?.items || []).map((item) => ({
      ...item,
      reactions: item.reactions || [],
      comments: item.comments || [],
    }));
    if (
      Array.isArray(payload?.availableReactions) &&
      payload.availableReactions.length
    ) {
      availableReactions.value = payload.availableReactions;
    }
    if (payload?.maxTextLength) maxTextLength.value = payload.maxTextLength;
    if (payload?.maxCommentTextLength) {
      maxCommentTextLength.value = payload.maxCommentTextLength;
    }
    pagination.value = payload?.pagination || {
      page,
      pageSize: PAGE_SIZE,
      totalItems: posts.value.length,
      totalPages: 1,
      hasNext: false,
    };
    currentPage.value = pagination.value.page || page;
  } catch (error) {
    listError.value = error?.message || "Could not load the board.";
    posts.value = [];
  } finally {
    listLoading.value = false;
  }
};

const changePage = (delta) => {
  const next = currentPage.value + delta;
  if (next < 1 || next > totalPages.value) return;
  openReactionPicker.value = null;
  editingPostId.value = null;
  editingCommentId.value = null;
  loadPosts(next);
};

const submitPost = async () => {
  const text = draft.value.trim();
  if (!text || submitting.value) return;

  submitting.value = true;
  submitError.value = "";
  try {
    await createDashboardPost(text);
    draft.value = "";
    await loadPosts(1);
  } catch (error) {
    submitError.value = error?.message || "Could not submit your answer.";
  } finally {
    submitting.value = false;
  }
};

const applyReactionLocally = (post, emoji) => {
  const group = post.reactions.find((reaction) => reaction.emoji === emoji);

  if (!group) {
    post.reactions = [
      ...post.reactions,
      { emoji, count: 1, reacted: true, admins: [myName.value] },
    ].sort(
      (a, b) =>
        availableReactions.value.indexOf(a.emoji) -
        availableReactions.value.indexOf(b.emoji)
    );
    return;
  }

  if (group.reacted) {
    group.count -= 1;
    group.reacted = false;
    const mine = group.admins.indexOf(myName.value);
    if (mine !== -1) group.admins.splice(mine, 1);
    if (group.count <= 0) {
      post.reactions = post.reactions.filter(
        (reaction) => reaction.emoji !== emoji
      );
    }
    return;
  }

  group.count += 1;
  group.reacted = true;
  group.admins = [...group.admins, myName.value];
};

const react = async (post, emoji) => {
  openReactionPicker.value = null;
  applyReactionLocally(post, emoji);
  try {
    await toggleDashboardPostReaction(post.id, emoji);
  } catch (error) {
    applyReactionLocally(post, emoji);
    listError.value = error?.message || "Could not save your reaction.";
  }
};

const submitComment = async (post) => {
  const text = (commentDrafts[post.id] || "").trim();
  if (!text || commentSubmitting[post.id]) return;

  commentSubmitting[post.id] = true;
  commentErrors[post.id] = "";
  try {
    await createDashboardPostComment(post.id, text);
    commentDrafts[post.id] = "";
    expandedComments[post.id] = true;
    await loadPosts(currentPage.value);
  } catch (error) {
    commentErrors[post.id] = error?.message || "Could not post your comment.";
  } finally {
    commentSubmitting[post.id] = false;
  }
};

const startPostEdit = (post) => {
  editingPostId.value = post.id;
  postEditDraft.value = post.text;
  postEditError.value = "";
};

const cancelPostEdit = () => {
  editingPostId.value = null;
  postEditDraft.value = "";
  postEditError.value = "";
};

const savePostEdit = async (post) => {
  const text = postEditDraft.value.trim();
  if (!text || postEditSaving.value) return;
  if (text === post.text) {
    cancelPostEdit();
    return;
  }

  postEditSaving.value = true;
  postEditError.value = "";
  try {
    await updateDashboardPost(post.id, text);
    cancelPostEdit();
    await loadPosts(currentPage.value);
  } catch (error) {
    postEditError.value = error?.message || "Could not save your changes.";
  } finally {
    postEditSaving.value = false;
  }
};

const removePost = async (post) => {
  const confirmed = window.confirm("Delete this answer?");
  if (!confirmed) return;

  try {
    await deleteDashboardPost(post.id);
    const isLastOnPage = posts.value.length === 1 && currentPage.value > 1;
    await loadPosts(isLastOnPage ? currentPage.value - 1 : currentPage.value);
  } catch (error) {
    listError.value = error?.message || "Could not delete this answer.";
  }
};

const startCommentEdit = (comment) => {
  editingCommentId.value = comment.id;
  commentEditDraft.value = comment.text;
  commentEditError.value = "";
};

const cancelCommentEdit = () => {
  editingCommentId.value = null;
  commentEditDraft.value = "";
  commentEditError.value = "";
};

const saveCommentEdit = async (post, comment) => {
  const text = commentEditDraft.value.trim();
  if (!text || commentEditSaving.value) return;
  if (text === comment.text) {
    cancelCommentEdit();
    return;
  }

  commentEditSaving.value = true;
  commentEditError.value = "";
  try {
    await updateDashboardPostComment(post.id, comment.id, text);
    cancelCommentEdit();
    await loadPosts(currentPage.value);
  } catch (error) {
    commentEditError.value = error?.message || "Could not save your changes.";
  } finally {
    commentEditSaving.value = false;
  }
};

const removeComment = async (post, comment) => {
  const confirmed = window.confirm("Delete this comment?");
  if (!confirmed) return;

  try {
    await deleteDashboardPostComment(post.id, comment.id);
    await loadPosts(currentPage.value);
  } catch (error) {
    commentErrors[post.id] =
      error?.message || "Could not delete this comment.";
  }
};

const toggleReactionPicker = (postId) => {
  openReactionPicker.value =
    openReactionPicker.value === postId ? null : postId;
};

onMounted(() => loadPosts(1));
</script>

<template>
  <div class="py-4 container-fluid">
    <div class="row justify-content-center">
      <div class="col-12 col-xl-9">
        <div class="card shadow-sm border-0 mb-4">
          <div class="card-body">
            <div class="text-center mb-4">
              <h1 class="display-6 font-weight-bolder mb-2">
                How have we made finding a partner easier for Iranians today?
              </h1>
              <p class="text-sm text-secondary mb-0">
                Every answer the team shares lands on this board.
              </p>
            </div>

            <textarea v-model="draft" class="form-control" rows="3" :maxlength="maxTextLength" placeholder="Today we…"
              @keydown.ctrl.enter="submitPost" @keydown.meta.enter="submitPost"></textarea>

            <div class="d-flex align-items-center flex-wrap gap-2 mt-3">
              <p class="mb-0 text-xs text-secondary">
                {{ remainingChars }} characters left · Ctrl+Enter to submit
              </p>
              <button type="button" class="btn bg-gradient-primary btn-sm mb-0 ms-auto"
                :disabled="submitting || !draft.trim()" @click="submitPost">
                {{ submitting ? "Sharing…" : "Share" }}
              </button>
            </div>

            <p v-if="submitError" class="text-xs text-danger mb-0 mt-2">
              {{ submitError }}
            </p>
          </div>
        </div>

        <div v-if="listError" class="alert alert-danger text-white text-sm">
          {{ listError }}
        </div>

        <div v-if="listLoading" class="text-center text-sm text-secondary py-5">
          Loading the board…
        </div>

        <div v-else-if="!posts.length" class="card shadow-sm border-0 text-center py-5">
          <p class="mb-0 text-sm text-secondary">
            Nothing here yet. Be the first to answer.
          </p>
        </div>

        <template v-else>
          <div v-for="post in posts" :key="post.id" class="card shadow-sm border-0 mb-3">
            <div class="card-body">
              <div class="d-flex align-items-center gap-2 mb-2">
                <div
                  class="avatar avatar-sm rounded-circle bg-gradient-dark text-white text-xxs d-flex align-items-center justify-content-center">
                  {{ initialsOf(post.author.name) }}
                </div>
                <div>
                  <p class="mb-0 text-sm font-weight-bold">
                    {{ post.author.name }}
                    <span v-if="post.isMine" class="badge bg-light text-dark border text-xxs ms-1">
                      You
                    </span>
                  </p>
                  <p class="mb-0 text-xs text-secondary">
                    {{ formatWhen(post.createdAt) }}
                    <span v-if="post.isEdited" :title="formatWhen(post.updatedAt)">· edited</span>
                  </p>
                </div>

                <div v-if="post.canEdit || post.canDelete" class="ms-auto d-flex gap-3">
                  <button v-if="post.canEdit && editingPostId !== post.id" type="button"
                    class="btn btn-link p-0 mb-0 text-xs text-secondary" title="Edit" aria-label="Edit"
                    @click="startPostEdit(post)">
                    <i class="fas fa-pen"></i>
                  </button>
                  <button v-if="post.canDelete" type="button" class="btn btn-link p-0 mb-0 text-xs text-danger"
                    title="Delete" aria-label="Delete" @click="removePost(post)">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>

              <div v-if="editingPostId === post.id" class="mb-3">
                <textarea v-model="postEditDraft" class="form-control" rows="3" :maxlength="maxTextLength"></textarea>
                <div class="d-flex gap-2 mt-2">
                  <button type="button" class="btn bg-gradient-primary btn-sm mb-0"
                    :disabled="postEditSaving || !postEditDraft.trim()" @click="savePostEdit(post)">
                    {{ postEditSaving ? "Saving…" : "Save" }}
                  </button>
                  <button type="button" class="btn btn-outline-secondary btn-sm mb-0" :disabled="postEditSaving"
                    @click="cancelPostEdit">
                    Cancel
                  </button>
                </div>
                <p v-if="postEditError" class="text-xs text-danger mb-0 mt-2">
                  {{ postEditError }}
                </p>
              </div>

              <p v-else class="text-sm mb-3" style="white-space: pre-wrap">
                {{ post.text }}
              </p>

              <div class="d-flex align-items-center flex-wrap gap-2 position-relative">
                <button v-for="reaction in post.reactions" :key="reaction.emoji" type="button"
                  class="btn btn-sm mb-0 py-1 px-2 border" :class="reaction.reacted
                    ? 'bg-gradient-primary text-white border-0'
                    : 'btn-outline-secondary'
                    " :title="reaction.admins.join(', ')" @click="react(post, reaction.emoji)">
                  {{ reaction.emoji }}
                  <span class="text-xs ms-1">{{ reaction.count }}</span>
                </button>

                <button type="button" class="btn btn-sm btn-outline-secondary mb-0 py-1 px-2"
                  @click="toggleReactionPicker(post.id)">
                  <span class="text-xs">+ React</span>
                </button>

                <div v-if="openReactionPicker === post.id"
                  class="position-absolute bg-white border rounded-3 shadow-sm p-2 d-flex gap-1"
                  style="top: 100%; left: 0; margin-top: 4px; z-index: 5">
                  <button v-for="emoji in availableReactions" :key="emoji" type="button" class="btn btn-link p-1 mb-0"
                    @click="react(post, emoji)">
                    {{ emoji }}
                  </button>
                </div>
              </div>

              <div class="border-top mt-3 pt-3">
                <button v-if="hiddenCommentCount(post)" type="button" class="btn btn-link p-0 mb-2 text-xs"
                  @click="expandedComments[post.id] = true">
                  Show {{ hiddenCommentCount(post) }} earlier comment{{
                    hiddenCommentCount(post) === 1 ? "" : "s"
                  }}
                </button>

                <div v-for="comment in visibleComments(post)" :key="comment.id" class="d-flex gap-2 mb-2">
                  <div
                    class="avatar avatar-xs rounded-circle bg-light text-dark text-xxs d-flex align-items-center justify-content-center flex-shrink-0">
                    {{ initialsOf(comment.author.name) }}
                  </div>
                  <div class="bg-light rounded-3 px-3 py-2 flex-grow-1">
                    <p class="mb-0 text-xs d-flex align-items-center flex-wrap gap-2">
                      <span class="font-weight-bold">
                        {{ comment.author.name }}
                      </span>
                      <span class="text-secondary">
                        {{ formatWhen(comment.createdAt) }}
                        <span v-if="comment.isEdited" :title="formatWhen(comment.updatedAt)">· edited</span>
                      </span>
                      <span v-if="comment.canEdit || comment.canDelete" class="ms-auto d-flex gap-3">
                        <button v-if="comment.canEdit && editingCommentId !== comment.id" type="button"
                          class="btn btn-link p-0 mb-0 text-xs text-secondary" title="Edit" aria-label="Edit"
                          @click="startCommentEdit(comment)">
                          <i class="fas fa-pen"></i>
                        </button>
                        <button v-if="comment.canDelete" type="button" class="btn btn-link p-0 mb-0 text-xs text-danger"
                          title="Delete" aria-label="Delete" @click="removeComment(post, comment)">
                          <i class="fas fa-trash"></i>
                        </button>
                      </span>
                    </p>

                    <div v-if="editingCommentId === comment.id" class="mt-2">
                      <input v-model="commentEditDraft" type="text" class="form-control form-control-sm"
                        :maxlength="maxCommentTextLength" @keydown.enter="saveCommentEdit(post, comment)" />
                      <div class="d-flex gap-2 mt-2">
                        <button type="button" class="btn bg-gradient-primary btn-sm mb-0"
                          :disabled="commentEditSaving || !commentEditDraft.trim()"
                          @click="saveCommentEdit(post, comment)">
                          {{ commentEditSaving ? "Saving…" : "Save" }}
                        </button>
                        <button type="button" class="btn btn-outline-secondary btn-sm mb-0"
                          :disabled="commentEditSaving" @click="cancelCommentEdit">
                          Cancel
                        </button>
                      </div>
                      <p v-if="commentEditError" class="text-xs text-danger mb-0 mt-1">
                        {{ commentEditError }}
                      </p>
                    </div>

                    <p v-else class="mb-0 text-sm" style="white-space: pre-wrap">
                      {{ comment.text }}
                    </p>
                  </div>
                </div>

                <div class="d-flex gap-2 mt-2">
                  <input v-model="commentDrafts[post.id]" type="text" class="form-control form-control-sm"
                    :maxlength="maxCommentTextLength" placeholder="Write a comment…"
                    @keydown.enter="submitComment(post)" />
                  <button type="button" class="btn btn-outline-primary btn-sm mb-0" :disabled="commentSubmitting[post.id] ||
                    !(commentDrafts[post.id] || '').trim()
                    " @click="submitComment(post)">
                    Send
                  </button>
                </div>

                <p v-if="commentErrors[post.id]" class="text-xs text-danger mb-0 mt-1">
                  {{ commentErrors[post.id] }}
                </p>
              </div>
            </div>
          </div>

          <div class="d-flex align-items-center flex-wrap gap-2 pt-2">
            <p class="mb-0 text-xs text-secondary">
              {{ pagination.totalItems }} answer{{
                pagination.totalItems === 1 ? "" : "s"
              }}
            </p>
            <div class="ms-auto d-flex align-items-center gap-2">
              <span class="text-xs text-secondary">
                Page {{ currentPage }} of {{ totalPages }}
              </span>
              <div class="btn-group btn-group-sm">
                <button type="button" class="btn btn-outline-secondary" :disabled="listLoading || currentPage <= 1"
                  @click="changePage(-1)">
                  Previous
                </button>
                <button type="button" class="btn btn-outline-secondary"
                  :disabled="listLoading || currentPage >= totalPages" @click="changePage(1)">
                  Next
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
